/**
 * One shared SMS keyword matcher (audit W1 #10 / #49). PURE: no IO.
 *
 * Every inbound path (Twilio fast ack TwiML, inbound-sms route, Conversations
 * route, inbound consent clear, line health, email reply STOP) reads opt-out /
 * opt-in / help through here so they can never disagree.
 *
 * Rules:
 *  - Opt-out is broad and wins over everything. Case and punctuation are
 *    ignored ("Stop.", "STOP!!", "opt-out", "Stop all", "un-subscribe").
 *    A message whose first word is an opt-out word is an opt-out.
 *  - Opt-in (START/UNSTOP/YES/RESUBSCRIBE) matches only the exact bare keyword:
 *    trimmed and case-insensitive, with no punctuation or emoji stripped.
 *    "Yes!", "start.", "(yes)" and "Yes I'm interested" never re-subscribe
 *    anyone (never looser than the old first-token match).
 *  - HELP/INFO match only when the WHOLE message, ignoring punctuation, is
 *    that one keyword. "Info on 123 Main" is a normal reply for the broker.
 */

/** Carrier / Twilio default opt-out keywords plus OPTOUT and REVOKE. */
export const SMS_OPT_OUT_WORDS: ReadonlySet<string> = new Set([
  'stop',
  'stopall',
  'unsubscribe',
  'cancel',
  'end',
  'quit',
  'optout',
  'revoke',
])

export const SMS_OPT_IN_WORDS: ReadonlySet<string> = new Set(['start', 'unstop', 'yes', 'resubscribe'])

export const SMS_HELP_WORDS: ReadonlySet<string> = new Set(['help', 'info'])

/** Whole-message opt-out phrases that do not start with an opt-out word. */
const SMS_OPT_OUT_PHRASES: ReadonlySet<string> = new Set([
  'remove me',
  'remove me from your list',
  'remove me from this list',
  'opt me out',
  'take me off',
  'take me off your list',
  'take me off this list',
  'do not text',
  'do not text me',
  'dont text',
  'dont text me',
  'don t text me',
  'no more texts',
  'no more text messages',
])

/** Polite filler that may precede a keyword ("please stop", "pls unsubscribe"). */
const LEADING_FILLER: ReadonlySet<string> = new Set(['please', 'pls', 'plz', 'kindly', 'ok', 'okay'])

/** Lowercase, every non-alphanumeric run becomes one space, trimmed. */
export function normalizeSmsBody(body: string | null | undefined): string {
  return String(body ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

function tokensOf(body: string | null | undefined): string[] {
  const n = normalizeSmsBody(body)
  return n ? n.split(' ') : []
}

/**
 * True when the whole message, ignoring case and punctuation, is one opt-out
 * keyword ("STOP", "Stop.", "stop all", "opt-out", "Un-subscribe!").
 * This is the narrow form Twilio itself applies at the carrier level.
 */
export function isSmsOptOutKeywordOnly(body: string | null | undefined): boolean {
  const tokens = tokensOf(body)
  if (tokens.length === 0) return false
  if (tokens.length === 1) return SMS_OPT_OUT_WORDS.has(tokens[0])
  const joined = tokens.join('')
  if (SMS_OPT_OUT_WORDS.has(joined)) return true
  return tokens.every((t) => SMS_OPT_OUT_WORDS.has(t) || t === 'all')
    && tokens.some((t) => SMS_OPT_OUT_WORDS.has(t))
}

/**
 * True when the inbound SMS is an opt-out. Broad on purpose (consent only
 * gets stricter): the first word (after polite filler) is an opt-out word,
 * the first two words join to one ("opt out", "un subscribe"), the whole
 * message is opt-out keywords, or a known "remove me" phrase.
 */
export function isSmsOptOut(body: string | null | undefined): boolean {
  const all = tokensOf(body)
  if (all.length === 0) return false
  if (isSmsOptOutKeywordOnly(body)) return true
  let i = 0
  while (i < all.length - 1 && LEADING_FILLER.has(all[i])) i++
  const tokens = all.slice(i)
  if (SMS_OPT_OUT_WORDS.has(tokens[0])) return true
  if (tokens.length >= 2 && SMS_OPT_OUT_WORDS.has(tokens[0] + tokens[1])) return true
  return SMS_OPT_OUT_PHRASES.has(tokens.join(' '))
}

/** Opt-in form: trimmed and lowercased only. No punctuation or emoji stripping. */
function bareOptInBody(body: string | null | undefined): string {
  return String(body ?? '').trim().toLowerCase()
}

/**
 * Exact bare START / UNSTOP / YES / RESUBSCRIBE (trimmed, case-insensitive).
 * Lifting a STOP is consent-granting, so this is the strictest form: any
 * punctuation, emoji or extra word means it is not an opt-in. Never true
 * for an opt-out.
 */
export function isSmsOptIn(body: string | null | undefined): boolean {
  if (isSmsOptOut(body)) return false
  return SMS_OPT_IN_WORDS.has(bareOptInBody(body))
}

/** The opt-in keyword itself ('start' | 'unstop' | 'yes' | 'resubscribe') or null. */
export function smsOptInKeyword(body: string | null | undefined): string | null {
  return isSmsOptIn(body) ? bareOptInBody(body) : null
}

/** Exact whole-message HELP / INFO. Never true for an opt-out. */
export function isSmsHelp(body: string | null | undefined): boolean {
  if (isSmsOptOut(body)) return false
  return SMS_HELP_WORDS.has(normalizeSmsBody(body))
}

export type SmsKeyword = 'opt-out' | 'opt-in' | 'help' | null

/** Classify one inbound body. Opt-out always wins. */
export function classifySmsKeyword(body: string | null | undefined): SmsKeyword {
  if (isSmsOptOut(body)) return 'opt-out'
  if (isSmsHelp(body)) return 'help'
  if (isSmsOptIn(body)) return 'opt-in'
  return null
}
